import React, { useCallback, useMemo } from "react";
import type { Trade } from "../../../../../../models";
import { naturalCompare } from "../../../../../../utils/sort";
import { useDropZone } from "../useDropZone";
import TradeChip from "../TradeChip/TradeChip";
import type { BucketCode } from "../TradeBuckets";
import "./TradeBucket.css";

interface Props {
  trade: Trade;
  /** Per-(code, section) entries currently (staged-)assigned to this trade. */
  codes: BucketCode[];
  expanded: boolean;
  onToggleExpand: () => void;
  onAssign: (ids: string[], tradeId: number) => void;
  onStartEdit: (trade: Trade) => void;
  /** Remove a code from one section's trade. */
  onRemove: (id: string, section: string) => void;
}

/** One trade — a drop target whose header shows swatch · name · count,
 *  expandable to the per-section codes it holds. */
const TradeBucket: React.FC<Props> = ({
  trade,
  codes,
  expanded,
  onToggleExpand,
  onAssign,
  onStartEdit,
  onRemove,
}) => {
  const handleDrop = useCallback(
    (ids: string[]) => onAssign(ids, trade.id),
    [onAssign, trade.id]
  );
  const { dragOver, handleDragOver, handleDragLeave, handleDrop: onDrop } =
    useDropZone(handleDrop);

  const bySection = useMemo(() => {
    const m = new Map<string, BucketCode[]>();
    for (const c of codes) {
      const s = c.section || "— no section —";
      const arr = m.get(s);
      if (arr) arr.push(c);
      else m.set(s, [c]);
    }
    return Array.from(m.entries()).sort((a, b) => naturalCompare(a[0], b[0]));
  }, [codes]);

  return (
    <div
      className={`tb-bucket ${dragOver ? "is-drag-over" : ""}`}
      style={{ borderColor: dragOver ? trade.fill_color : trade.fill_color + "80" }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={onDrop}
    >
      <div className="tb-bucket-head" onClick={onToggleExpand}>
        <span className="tb-bucket-swatch" style={{ backgroundColor: trade.fill_color }} />
        <span className="tb-bucket-arrow">{expanded ? "▼" : "▶"}</span>
        <span className="tb-bucket-name">{trade.name}</span>
        {trade.id < 0 && (
          <span className="tb-bucket-new" title="Staged — created when you save">
            NEW
          </span>
        )}
        <span className="tb-bucket-count">({codes.length})</span>
        <button
          className="tb-bucket-icon"
          onClick={(e) => {
            e.stopPropagation();
            onStartEdit(trade);
          }}
          title="Edit trade name / color / delete"
          type="button"
        >
          &#9998;
        </button>
      </div>
      {expanded && (
        <div className="tb-bucket-content">
          {bySection.length === 0 && (
            <div className="tb-bucket-empty">No work codes — drag codes here</div>
          )}
          {bySection.map(([section, cs]) => (
            <div key={section} className="tb-bucket-section">
              <div className="tb-bucket-section-name">{section}</div>
              {cs.map((c) => (
                <TradeChip
                  key={`${c.id}|${c.section}`}
                  id={c.id}
                  code={c.code}
                  label={c.label}
                  section={c.section}
                  fill={trade.fill_color}
                  font={trade.font_color}
                  onRemove={onRemove}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TradeBucket;
